import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import useSWR from 'swr';
import { Loader2, MapPin, Clock, Globe, Star, Package as PackageIcon, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fetcher = (url) => fetch(url).then((r) => r.json());

const SYM = { AED: 'AED ', USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$' };
const sym = (c) => SYM[(c || 'USD').toUpperCase()] || `${c} `;
const fmtN = (n) => { const v = Number(n || 0); return Number.isInteger(v) ? v.toLocaleString() : v.toFixed(2); };

const priceLine = (s) => {
  if (s.durationModel === 'subscription') {
    const ivs = ((s.subscriptionConfig || {}).intervals || []).filter((i) => i && i.enabled !== false && Number(i.price) > 0);
    if (ivs.length) {
      const iv = ivs.reduce((a, b) => (Number(a.price) <= Number(b.price) ? a : b));
      return { amount: iv.price, unit: `/ ${{ week: 'week', month: 'month', year: 'year' }[iv.interval] || ''}` };
    }
  }
  const bm = s.bookingMode || s.capacityMode || '';
  return { amount: s.price || s.basePrice || 0, unit: bm === 'per_ticket' ? '/ person' : '' };
};

const durText = (s) => {
  if (s.durationModel === 'day_pass') return 'All-day';
  if (s.durationModel === 'flexible_dates') return 'Multi-night';
  if (s.durationModel === 'subscription') return 'Renews automatically';
  const m = s.duration || s.durationMinutes;
  if (!m) return null;
  const h = Math.floor(m / 60), mm = m % 60;
  return h > 0 ? `${h} hr${mm ? ` ${mm} min` : ''}` : `${m} min`;
};

/** Hosted brand page — the marketplace experience, scoped to ONE operator.
 *  /b/{orgSlug} → grid of services + packages → /b/{orgSlug}/e/{serviceId} */
export default function BrandPage() {
  const { orgSlug } = useParams();

  const { data, isLoading } = useSWR(
    orgSlug ? `${API_URL}/api/v2/public/services?org=${encodeURIComponent(orgSlug)}` : null,
    fetcher, { revalidateOnFocus: false, dedupingInterval: 30000 }
  );
  const { data: pkgData } = useSWR(
    orgSlug ? `${API_URL}/api/v2/public/packages?org=${encodeURIComponent(orgSlug)}` : null,
    fetcher, { revalidateOnFocus: false, dedupingInterval: 30000 }
  );
  const { data: eventsData } = useSWR(
    orgSlug ? `${API_URL}/api/v2/public/orgs/${orgSlug}/club-events` : null,
    fetcher, { revalidateOnFocus: false, dedupingInterval: 30000 }
  );
  const upcomingEvents = eventsData?.events || [];

  const org = data?.organization;
  const services = (data?.services || []).filter((s) => s.active !== false);
  const packages = pkgData?.packages || [];
  const currency = org?.currency || services[0]?.currency || 'USD';
  const s$ = sym(currency);
  const city = services.find((s) => s.city)?.city;

  useEffect(() => {
    if (org?.name) document.title = `${org.name} — Book online`;
  }, [org?.name]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="brand-page-loading">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center px-4" data-testid="brand-page-not-found">
        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
          <Globe className="w-10 h-10 text-gray-400" />
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Page not found</h1>
        <p className="text-gray-500">This booking page may have moved or is no longer available.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="brand-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md pt-[env(safe-area-inset-top)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2.5">
              {org.logoUrl ? (
                <img src={org.logoUrl} alt={org.name} className="h-9 w-9 object-contain rounded-lg bg-white" />
              ) : (
                <div className="h-9 w-9 rounded-lg bg-[#04091B] flex items-center justify-center">
                  <span className="text-white font-semibold">{org.name?.charAt(0)}</span>
                </div>
              )}
              <span className="text-lg font-bold text-gray-900">{org.name}</span>
            </div>
            {org.website && org.website !== 'https://bookingflows.com' && (
              <a href={org.website} target="_blank" rel="noopener noreferrer"
                 className="text-sm text-gray-500 hover:text-gray-800 transition-colors hidden sm:inline-flex items-center gap-1.5"
                 data-testid="brand-website-link">
                <Globe className="w-4 h-4" /> Website
              </a>
            )}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900" data-testid="brand-page-title">{org.name}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-2.5 text-gray-500">
            {city && (
              <span className="inline-flex items-center gap-1.5 text-sm">
                <MapPin className="w-4 h-4 text-blue-500" /> {city}
              </span>
            )}
            <span className="text-sm">{services.length} experience{services.length === 1 ? '' : 's'}</span>
            {upcomingEvents.length > 0 && (
              <Link to={`/b/${orgSlug}/events`} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:text-blue-700" data-testid="brand-events-link">
                Event calendar ({upcomingEvents.length}) <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
            {org.showGoogleReviews && org.googleReviewCount > 0 && (
              <span className="inline-flex items-center gap-1 text-sm" data-testid="brand-google-reviews">
                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                {org.googleReviewCount.toLocaleString()} Google reviews
              </span>
            )}
          </div>
        </div>

        {/* Packages */}
        {packages.length > 0 && (
          <div className="mb-10" data-testid="brand-packages-section">
            <div className="flex items-center gap-2 mb-4">
              <PackageIcon className="w-5 h-5 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-900">Packages</h2>
              <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 rounded-full px-2.5 py-1">Bundle &amp; save</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {packages.map((p) => {
                const imgs = (p.images && p.images.length ? p.images : [p.imageUrl]).filter(Boolean).slice(0, 3);
                const pct = Math.round(p.discountPercent || 0);
                return (
                  <Link key={p.id} to={`/book?org=${orgSlug}`} target="_blank"
                        className="group bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_rgba(2,12,27,0.08)] hover:shadow-[0_10px_32px_rgba(2,12,27,0.14)] transition-shadow"
                        data-testid={`brand-package-card-${p.id}`}>
                    <div className="relative h-44 bg-[#04091B]">
                      {imgs.length >= 3 ? (
                        <div className="grid grid-cols-3 gap-0.5 h-full">
                          <img src={imgs[0]} alt={p.name} className="col-span-2 w-full h-full object-cover" loading="lazy" />
                          <div className="grid grid-rows-2 gap-0.5 min-h-0">
                            <img src={imgs[1]} alt="" className="w-full h-full object-cover min-h-0" loading="lazy" />
                            <img src={imgs[2]} alt="" className="w-full h-full object-cover min-h-0" loading="lazy" />
                          </div>
                        </div>
                      ) : imgs.length === 2 ? (
                        <div className="grid grid-cols-2 gap-0.5 h-full">
                          <img src={imgs[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                          <img src={imgs[1]} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ) : imgs[0] ? (
                        <img src={imgs[0]} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                      ) : null}
                      {(p.services || []).length > 1 && (
                        <span className="absolute bottom-2.5 left-2.5 bg-[#04091B]/75 text-white text-[11px] font-bold px-2.5 py-1 rounded-full backdrop-blur-sm">
                          {(p.services || []).length} experiences
                        </span>
                      )}
                      {pct > 0 && (
                        <span className="absolute top-2.5 right-2.5 bg-emerald-600 text-white text-[11px] font-extrabold px-2.5 py-1 rounded-full">
                          Save {pct}%
                        </span>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="font-bold text-gray-900">{p.name}</p>
                      <p className="text-xs text-gray-500 mt-1 line-clamp-1">{(p.services || []).map((s) => s.name).join(' + ')}</p>
                      <div className="flex items-center justify-between mt-3">
                        <p className="text-sm font-semibold text-gray-900">
                          {pct > 0 && <span className="text-gray-400 line-through font-normal text-xs mr-1.5">{s$}{fmtN(p.combinedFromPrice)}</span>}
                          From {s$}{fmtN(p.packageFromPrice || p.combinedFromPrice)}
                        </p>
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 group-hover:gap-1.5 transition-all">
                          Book package <ArrowRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Services grid */}
        <div data-testid="brand-services-section">
          {packages.length > 0 && <h2 className="text-xl font-bold text-gray-900 mb-4">All experiences</h2>}
          {services.length === 0 ? (
            <div className="bg-gray-50 rounded-3xl p-12 text-center" data-testid="brand-services-empty">
              <p className="text-gray-500">No experiences available right now — check back soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {services.map((s) => {
                const img = (s.images || [])[s.mainImageIndex || 0] || (s.images || [])[0];
                const pr = priceLine(s);
                const dur = durText(s);
                return (
                  <Link key={s.id} to={`/b/${orgSlug}/e/${s.id}`}
                        className="group bg-white rounded-3xl overflow-hidden shadow-[0_4px_20px_rgba(2,12,27,0.07)] hover:shadow-[0_10px_32px_rgba(2,12,27,0.13)] transition-shadow"
                        data-testid={`brand-service-card-${s.id}`}>
                    <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
                      {img ? (
                        <img src={img} alt={s.name} loading="lazy"
                             className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#04091B]">
                          <Globe className="w-10 h-10 text-white/20" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <p className="font-bold text-gray-900 leading-snug">{s.name}</p>
                      {s.tagline && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{s.tagline}</p>}
                      {dur && (
                        <p className="inline-flex items-center gap-1.5 text-xs text-gray-400 mt-2">
                          <Clock className="w-3.5 h-3.5" /> {dur}
                        </p>
                      )}
                      <p className="text-sm font-semibold text-gray-900 mt-2">
                        From {s$}{fmtN(pr.amount)} {pr.unit && <span className="text-xs font-normal text-gray-400">{pr.unit}</span>}
                      </p>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-12 py-10 px-4 border-t border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm font-semibold text-gray-900">{org.name}</p>
          <a href="https://bookingflows.com" target="_blank" rel="noopener noreferrer"
             className="text-sm text-gray-400 hover:text-gray-600 transition-colors" data-testid="brand-powered-by">
            Powered by <span className="font-semibold">BookingFlows</span>
          </a>
        </div>
      </footer>
    </div>
  );
}
